import { createContext, useContext, type ReactNode } from 'react';

/**
 * Die offenen Gestaltungsfragen des Werkbank-Umbaus, als Schalter.
 *
 * WOZU DAS HIER STEHT
 *
 * Wir haben mehrfach ein Mockup gebaut, es freigegeben, umgesetzt - und das
 * Ergebnis wich ab. Der Grund war nie Schlamperei bei der Umsetzung, sondern
 * dass das Mockup aus anderem Material bestand als die App: freie Farbwerte
 * statt Tokens, schoene Titel statt echter, ein Zustand statt sechs, und keine
 * der Zwaenge, die in der App wirklich gelten (Hoehenkette, ausklappbare
 * Seitenleiste, Tastaturvertraege, Tests).
 *
 * Dieser Kontext dreht das um. Der Entwurf laeuft in der echten App, mit den
 * echten Bauteilen und den echten Daten des Nutzers. Was hier nicht geht, geht
 * auch im Entwurf nicht, und zwar sofort statt zwei Wochen spaeter.
 *
 * DIE WICHTIGSTE EIGENSCHAFT: die Voreinstellung ist das HEUTIGE Verhalten.
 * Ohne Provider liest jedes Bauteil `STANDARD` und verhaelt sich exakt wie
 * bisher. Die Entwurfsseite ist der einzige Ort, der andere Werte setzt. Ein
 * vergessener Provider kann deshalb nichts kaputtmachen, und der Bestand
 * braucht keinen einzigen Testabgleich.
 */
export interface EntwurfOptionen {
  /**
   * `klassisch` - Seitentitel, Reiterzeile und Filterkachel untereinander,
   * wie heute. `eineZeile` - Reiter, Suche, Filterzugang, Ansicht und die
   * beiden Aktionsknoepfe auf einer 40 px hohen Zeile.
   */
  kopfzone: 'klassisch' | 'eineZeile';

  /**
   * `sichtbar` - der h1 steht als eigener Block ueber der Seite. `nurSchmal` -
   * er bleibt im Baum, wird aber ab 768 px unsichtbar. Die Schwelle ist
   * zeichengleich die des Brotkrumen-Containers in `Layout.tsx`: der Titel
   * verschwindet genau dort, wo die Brotkrume ihn wortgleich traegt, und
   * bleibt sichtbar, wo sie es nicht tut.
   */
  seitentitel: 'sichtbar' | 'nurSchmal';

  /**
   * Wie der Auslieferungsstand in der PRODUKTLEISTE erscheint (nicht in der
   * Karte, die zeigt ihn immer im Klartext).
   *
   * `pille` - wie heute, Klartext in der Abzeichenspalte. Kostet Titelbreite:
   * die laengste Pille misst 144 px, dem Titel bleiben dann 29 von 300 px.
   * `marker` - ein 7 px breiter Punkt in der Markenzeile, Klartext im
   * zugaenglichen Namen. Kostet null Titelbreite.
   * `keine` - so zeigt es das freigegebene C2-Mockup. Bewusst waehlbar, aber
   * nicht empfohlen: der Zustand "Schon bei Google, ungeprueft" betrifft auf
   * zwei gemessenen Feeds 100 Prozent der angeblich offenen Produkte. Ohne
   * jedes Signal behauptet der Reiter "Offen" Arbeit, die keine ist.
   */
  statusAnzeige: 'pille' | 'marker' | 'keine';

  /**
   * `lang` - "18 freigegebene Produkte zu Google uebertragen", wie heute.
   * `kurz` - "18 zu Google uebertragen", wie im Mockup. Der Unterschied sind
   * rund 130 px, und die entscheiden gemessen darueber, ob die einzeilige
   * Kopfzone bei ausgeklappter Seitenleiste umbricht. Der Preis ist das Wort
   * "freigegeben".
   */
  uploadBeschriftung: 'lang' | 'kurz';

  /**
   * Breite der Produktleiste in Pixeln. 300 ist der Bestand. 340 gibt dem
   * Titel 40 px mehr, die vollstaendig der Feed-Spalte der Karte abgehen.
   */
  leistenBreite: 300 | 340;

  /**
   * Wie die drei Statusreiter aussehen.
   *
   * `unterstrich` - nackter Text, der aktive Reiter traegt eine 2-px-Linie
   * darunter. So ist es heute. Das Muster stammt aus der Zeit, als die Reiter
   * eine eigene Zeile hatten: ein Unterstrich braucht eine Grundlinie ueber
   * die volle Breite, an der er sitzt. In der einzeiligen Kopfzone endet
   * diese Linie nach rund 335 px mitten in der Zeile, und die Gruppe haengt
   * im Raum - sie ist ausserdem das EINZIGE Element der Zeile ohne Flaeche,
   * waehrend Suchfeld, Filterknopf, Ansicht und Upload alle einen Rahmen
   * tragen.
   *
   * `segmente` - eine Gruppe mit gemeinsamer Flaeche, der aktive Reiter als
   * gefuellte Kachel darin. Das ist das Hausmuster aus `ui/tabs.tsx`
   * (bg-muted p-1, aktiv bg-card shadow-sm), das Einstellungen und Roadmap
   * bereits benutzen, und es ist das, was das C2-Mockup zeigt.
   *
   * Rein visuell: role=tablist, role=tab, aria-selected, der Roving Tabindex
   * und der Pfeiltasten-Handler bleiben in beiden Faellen unveraendert.
   */
  reiterStil: 'unterstrich' | 'segmente';

  /**
   * `inKarte` - die Produktliste sitzt in derselben Flaeche wie die Karte,
   * getrennt nur durch einen 1-px-Strich. So ist es heute, und deshalb wirkt
   * die Liste unfertig: sie hat weder Rundung noch Rahmen noch eigenen
   * Hintergrund, waehrend die Karte daneben alle drei traegt.
   *
   * `eigeneKachel` - so zeigt es das freigegebene C2-Mockup: eine eigene
   * Kachel mit Rundung, Rahmen, Kartenflaeche und einem Spalt zur Karte.
   *
   * Die Klassen gehen an den BESTEHENDEN 300-px-Knoten, nie an einen neuen
   * Wrapper. Die Rail-Wurzel arbeitet mit h-full ohne eigene Hoehe und
   * funktioniert nur als Streckkind des Flex-Containers; ein unklassierter
   * Zwischenknoten loest das still gegen height:auto auf, und der
   * virtualisierte Scroller kollabiert.
   */
  listenKachel: 'inKarte' | 'eigeneKachel';

  /**
   * `oben` - eigene Zeile ueber der Karte, wie heute. `imKartenkopf` - die
   * Positionsangabe steht ohnehin schon rechts im Kartenkopf, die
   * Sitzungszaehler ziehen dorthin. Spart 52 px senkrecht.
   */
  fortschritt: 'oben' | 'imKartenkopf';

  /**
   * Die Beschreibungs-Textarea im Aufklapper.
   *
   * `wieHeute` - Diff UND Textarea stehen gleichzeitig da, die Textarea mit
   * `rows={8}`. Das ist der Zustand, den Etappe 4 hinterlassen hat, und er ist
   * ein Unfall: der Vorgaenger `DiffField` nahm den Editor als Prop und zeigte
   * ihn erst hinter "Anpassen" (Commit c1b8773). Der Schalter ist beim Umbau
   * abgerissen worden, nicht abgeschafft - der Fingerabdruck steht noch im
   * Code, denn die Textarea traegt bis heute den zugaenglichen Namen
   * "Anpassen: Beschreibung", also den Namen eines Knopfes, den es nicht gibt.
   *
   * `hinterAnpassen` - der Diff steht allein, darunter ein "Anpassen"-Knopf,
   * und der ERSETZT sich beim Klick durch die Textarea. Dasselbe Muster wie
   * beim Titel und bei den Attribut-Chips. Null Pixel im Ruhebild, 186 px
   * sobald jemand den Aufklapper oeffnet.
   *
   * Sicher gegen Datenverlust, und zwar aus einem Grund, der beim Bauen
   * eingehalten werden MUSS: die Textarea schreibt bei jedem Anschlag ueber
   * `onEditChange` nach oben in `editState`. Das Schliessen verwirft deshalb
   * nichts. Ein Editor mit lokalem Entwurf, der erst auf "Fertig" hochgibt,
   * waere das Gegenteil - dann verwirft ein Klick daneben ihn still,
   * `getHasUnsavedChanges` sieht ihn nie, und die beforeunload-Warnung feuert
   * nicht.
   */
  beschreibungEditor: 'wieHeute' | 'hinterAnpassen';

  /**
   * Der durchgestrichene Merchant-Titel in der Shopping-Kachel.
   *
   * `zeigen` - wie heute, einzeilig und durchgestrichen ueber dem neuen Titel.
   * `weglassen` - weg, 14 px.
   *
   * Von den sieben Titelstellen der Karte ist das die entbehrlichste: derselbe
   * Text steht unverstellt im Kartenkopf und wortweise ausgezeichnet im Diff
   * der rechten Spalte. Was die Kachel dagegen als EINZIGE zeigt, ist der neue
   * Titel in der Form, in der der Kunde ihn sieht - zweizeilig geklammert.
   *
   * Politisch heikel, technisch nicht: die Kachel heisst "So sieht der Kunde
   * es", und beim Kunden steht der alte Titel nie. Wer ihn hier vermisst,
   * vermisst einen Vergleich, den die rechte Spalte genauer fuehrt.
   */
  kachelVorherTitel: 'zeigen' | 'weglassen';

  /**
   * Eine Marke am Titel im Kartenkopf.
   *
   * `ohne` - der Kopf zeigt den optimierten Titel unbeschriftet.
   * `mitOptimiert` - davor steht klein "Optimiert".
   *
   * Seit T3 (08.09.2026) zeigt der Kopf nicht mehr `product.title`, sondern
   * denselben `optimized_title` wie die Leistenzeile 8 px daneben - genau der
   * Feldtausch, den dieser Schalter urspruenglich als Marke ausgleichen
   * sollte. Der Fehler, gegen den die Marke 2026-08-23 gebaut wurde ("Kopf
   * und Leiste zeigen GEGENTEILIGE Felder, beide unmarkiert"), ist damit
   * behoben, nicht die Marke selbst ueberfluessig geworden: die Verwechslung
   * dreht sich nur um. Jetzt zeigen beide Stellen dasselbe Feld, und die
   * Marke sagt, WELCHER Stand das ist - der optimierte, nicht der Merchant-
   * Titel. Ohne sie liesse sich aus dem Kopf allein nicht mehr ablesen, ob
   * man den Kunden-Stand oder das Original vor sich hat.
   *
   * Kostet null Pixel: die Marke sitzt in der bestehenden Zeile vor dem Titel,
   * der ohnehin `truncate` traegt.
   *
   * Die Titelzeile zu LOESCHEN waere die Alternative und ist ausgeschlossen -
   * Spec 4.1 nennt sie woertlich, und sie traegt die Identitaet der Karte.
   */
  kopftitelMarke: 'ohne' | 'mitOptimiert';

  /**
   * Die Zeile unter der Kachel.
   *
   * `satz` - wie heute: "Die Kachel zeigt zwei Zeilen. Laengere Titel
   * schneidet Google ab." Zwei Zeilen, auf jeder Karte derselbe Text.
   * `gemesseneZahl` - "70 von 118 Zeichen sichtbar", also die Aussage ueber
   * DIESES Produkt, einzeilig. 12 px.
   *
   * Die Zahl ist dieselbe, die auch der Strich in der Kuerzungsmarke meint:
   * `splitAtCut` rechnet in CODEPUNKTEN. Sie darf nicht gegen `title.length`
   * (UTF-16) getauscht werden, sonst nennen zwei Stellen auf einer Karte
   * verschiedene Zahlen fuer dieselbe Grenze.
   *
   * Wird nicht gekuerzt, steht dort "Vollstaendig sichtbar" statt einer
   * Warnung ohne Anlass.
   */
  kuerzungsHinweisLinks: 'satz' | 'gemesseneZahl';

  /**
   * Wo die Kuerzungsmarke steht.
   *
   * `eigenerBlock` - wie heute: unter dem Diff ein eigener Kasten mit dem
   * ganzen neuen Titel noch einmal am Stueck, Strich und gedaempftem Rest.
   * Rund 70 px, und der neue Titel steht damit ZWEIMAL untereinander -
   * `after={editState.optimized_title}` im Diff und
   * `title={editState.optimized_title}` in der Kuerzungsmarke, identischer
   * Ausdruck, identischer Renderdurchlauf, getrennt nur durch den
   * 28-px-Knopf. (Bewusst ohne Zeilennummern: die vorherige Fassung nannte
   * welche, und sie waren beim naechsten Umbau bereits falsch - eine
   * Aktualisierung haette nur die Uhr zurueckgestellt.)
   *
   * `imNachherBlock` - der Strich sitzt IM Nachher-Block des Diffs. Der Titel
   * steht dann einmal. 70 px rechts.
   *
   * REVIDIERT ENTSCHEIDUNG E5
   * (docs/superpowers/plans/2026-08-22-werkbank-c2-karte.md:72). Deren
   * Begruendung war, ein Zeichenschnitt bei Position 70 laufe mitten durch ein
   * Token und zerreisse die Diff-Markierung. Sie traegt nicht mehr, seit
   * `splitTokensAt` das ueberkreuzende Token in zwei Teile DESSELBEN
   * Elementtyps teilt: ein <ins> wird zu zwei <ins>. Was sich aendert, ist die
   * Zahl der Elemente, nicht die Markierung.
   *
   * Die Alleinstellung der Marke bleibt: sie ist weiterhin die einzige Stelle,
   * die den abgeschnittenen Rest bei 38 Prozent Deckkraft SICHTBAR laesst. Der
   * erklaerende Satz "Ab dem Strich sieht der Kunde nichts mehr" wandert mit
   * unter den Diff.
   */
  /*
   * Dritter Wert `keine` (2026-08-24, Dennis): gar keine Marke im Titelblock.
   * Die Begruendung traegt, weil die Information zweifach anderswo steht: die
   * Kachel links SCHNEIDET den Titel sichtbar nach zwei Zeilen, und die Zeile
   * darunter nennt die Zahl ("70 von 132 Zeichen sichtbar",
   * `kuerzungsHinweisLinks: 'gemesseneZahl'`). Ein Strich im Feed-Text sagt
   * dann zum dritten Mal dasselbe. Im Bearbeiten-Modus bleibt der Zaehler
   * (`TitleCharCounter`) davon unberuehrt — er warnt vor der HARTEN
   * 150er-Grenze, nicht vor der Anzeige-Konvention.
   */
  kuerzungsAnzeige: 'eigenerBlock' | 'imNachherBlock' | 'keine';

  /**
   * Wer traegt die Aussage "diese Woerter entfielen"?
   *
   * `farbe` - wie heute die rote Flaeche im Vorher-Block.
   * `zaehlerImLabel` - eine Zahl im Feld-Label ("4 entfernt, 7 ergaenzt"), die
   * Flaeche faellt weg. 0 px, drei rote Flaechen weniger.
   *
   * DIE ENTFAERBUNG UND IHR ERSATZTRAEGER GEHOEREN IN DENSELBEN COMMIT. Der
   * Vorher-Block ist blockweit durchgestrichen; `BeforeAfterDiff` haelt selbst
   * fest, dass das <del> daran "keine Optik-Aenderung" bringt. Die rote
   * Flaeche ist damit heute der ALLEINIGE Traeger, und sie ohne Ersatz zu
   * nehmen ist ein WCAG-1.4.1-Bruch an drei Stellen je Karte.
   *
   * Der Zaehler, nicht die genannten Woerter (Entscheidung 2): 0 px statt 17,
   * und die Aenderungsliste bleibt frei. NIEMALS BEIDES - das erzeugte an
   * neuer Stelle genau die Doppelung, die dieser Umbau beendet.
   *
   * Der Zaehler erscheint auch dann, wenn nur `insMarke` die Farbe abgibt:
   * sonst gaebe es die Kombination "gruen weg, rot da, kein Zaehler", und die
   * Aussage "diese Woerter kamen dazu" haette gar keinen Traeger. Er nennt
   * immer beide Zahlen.
   */
  titelDiffTraeger: 'farbe' | 'zaehlerImLabel';

  /**
   * Ton der ERGAENZTEN Woerter im Titel-Diff.
   *
   * Angelegt in derselben Aufgabe wie `titelDiffTraeger`, weil der Zaehler an
   * beide gekoppelt ist. Der KI-Ton der App ist seit dem Petrol-Umbau (S3)
   * `primary` - die Wahl liegt damit nur noch zwischen Gruen und Neutral;
   * der ehemals dritte Wert `violett` ist mit D39 entfallen.
   */
  insMarke: 'gruen' | 'neutral';

  /**
   * Wie tief die Punkte-Spalte aufgeschlagen ist.
   *
   * `immerOffen` - wie heute: Gesamtzahl, dann sechs Zeilen mit Balken und
   * Delta. Rund 276 px, und damit groesser als die Produktkachel mit 249 px,
   * die es erklaeren soll.
   * `aufklapperMitBefund` - Gesamtzahl bleibt, die sechs Zeilen wandern in
   * einen Aufklapper. 215 px und 9 Farbflaechen weniger.
   * `eineZeile` - Spec 5.2: die linke Spalte traegt EINE Zeile "Punkte 100 →
   * 100" (`score.oneLine`), keine Versalien-Ueberschrift mehr. Sechsmal
   * "±0" untereinander ist der Normalfall und sagt nichts - deshalb ist die
   * Zeile NUR dann ein Aufklapper, wenn mindestens eine der sechs
   * Komponenten tatsaechlich abweicht; sonst ein statischer Absatz ohne
   * Knopf, ohne `aria-expanded`, ohne Chevron. Neuer STANDARD-Wert seit der
   * Feed-Check/Werkbank-Entlastung (T4, 08.09.2026).
   *
   * WIDERSPRICHT SPEC 4.2 WOERTLICH ("Immer sichtbar, nie aufklappbar") und
   * Abbauliste 13 Position 2. Die Begruendung, die traegt: der Grund der Regel
   * war, dass die PUNKTZAHL dreimal auf der Karte stand, eine davon erst nach
   * einem Klick. Nach diesem Schritt steht sie genau einmal und permanent
   * sichtbar. GEFALTET WIRD DIE DIAGNOSE, NICHT DAS URTEIL.
   *
   * BEDINGUNG: der geschlossene Kopf MUSS den schwaechsten Bereich nennen.
   * Sonst zwingt er zum Klick, um zu erfahren, ob der Klick lohnt - genau das,
   * was `CollapsibleField` im Haus schon abgeschafft hat.
   *
   * "Schwaechster Bereich" heisst groesster GEWICHTETER Verlust, nicht
   * kleinster Rohwert (Hausregel aus `scoreBreakdown.ts`).
   *
   * Die sechs Balken bleiben, wie sie sind. Sie pauschal auf einen Ton zu
   * setzen wuerde Design-Audit N5 umkehren.
   */
  punkteTiefe: 'immerOffen' | 'aufklapperMitBefund' | 'eineZeile';

  /**
   * Der senkrechte Rhythmus der rechten Spalte.
   *
   * `normal` - wie heute: `gap-4` zwischen den sieben Bloecken, `space-y-2`
   * zwischen den drei Aufklappern.
   * `kompakt` - `gap-2.5` und `space-y-1.5`. 20 bis 36 px, je nachdem, wie
   * viele Bloecke ueberhaupt erscheinen (Hinweisband und Aenderungsliste
   * rendern `null`, wenn sie nichts zu sagen haben).
   *
   * Die Klassen gehen an die BESTEHENDEN Knoten, nicht an neue Wrapper: ein
   * unklassierter Zwischenknoten loest die Hoehenkette still gegen
   * `height:auto` auf.
   */
  feedSpalteDichte: 'normal' | 'kompakt';

  /**
   * Die Highlight-Eingabefelder im Aufklapper.
   *
   * `wieHeute` - der Aufklapper zeigt sofort n Eingabefelder plus
   * Entfernen-Knoepfe plus "Hinzufuegen".
   * `hinterAnpassen` - er zeigt erst eine NUR-LESE-Liste mit den Marken "neu",
   * darunter einen "Anpassen"-Knopf, der die Felder holt.
   *
   * Die Nur-Lese-Liste ist keine Zutat, sondern die BEDINGUNG. Die Marke "neu"
   * ist der einzige Beleg fuer die Kopfzahl des Aufklappers ("3, davon 2
   * neu"); ohne sie steht dort eine Zahl ohne Nachweis. Drei Tests erwarten
   * sie direkt nach dem Aufklappen, und sie haben recht damit.
   *
   * Sie benutzt `listenSchluessel` - dieselbe Vergleichsregel wie die
   * Kopfzahl. Zwei Kopien lassen die Zahl oben den Marken unten widersprechen;
   * genau das war schon einmal kaputt.
   *
   * Gewinn: null Pixel im Ruhebild. Der Schalter steht zuletzt, weil sein
   * Verhaeltnis von Risiko zu Gewinn das schlechteste der zehn ist. Er zahlt
   * sich erst ein, sobald jemand den Aufklapper wirklich oeffnet.
   */
  highlightsEditor: 'wieHeute' | 'hinterAnpassen';

  /**
   * Wie die Gesamtpunktzahl der Karte erscheint.
   *
   * `zahl` - wie heute, die nackte Ziffer mit Vorher-Pfeil und Delta.
   * `ring` - derselbe Donut wie im Feed-Check (`HeroDonut`, verkleinert):
   * Wiedererkennung ueber die App hinweg, die Punktzahl als Bild statt als
   * Ziffer. Vorher-Wert und Delta bleiben daneben stehen. Kostet senkrecht
   * rund 40 px in der linken Spalte - wird gemessen, nicht geglaubt.
   */
  punkteAnzeige: 'zahl' | 'ring';

  /**
   * Die Marke an den "ergaenzt"-Chips.
   *
   * `punkt` - wie heute ein 6-px-Punkt in Violett.
   * `sparkle` - das Sparkle-Icon, dasselbe Zeichen, das die Aenderungsliste
   * fuer "das kam von der KI" benutzt. Ein Blick zeigt dann, wie viel die KI
   * beigetragen hat. Chips mit Zustand `missing` behalten ihren Warn-Punkt,
   * unveraenderte Chips bleiben ohne Marke.
   */
  chipMarke: 'punkt' | 'sparkle';

  /**
   * Der Leerzustand "alles freigegeben, wartet auf den Upload".
   *
   * `schlicht` - wie heute: Titel, Beschreibung mit der Wartezahl, fertig.
   * `erfolgsmoment` - dieselbe Auskunft, aber die Arbeit steht gross im Bild:
   * wie viele Produkte insgesamt freigegeben sind (aus den Reiterzahlen) und
   * der Feed-Durchschnittsscore aus dem juengsten Befund. Beide Zeilen fallen
   * weg statt "0" zu behaupten, wenn die Quelle nichts liefert - erfunden
   * wird hier nichts.
   */
  leerzustand: 'schlicht' | 'erfolgsmoment';

  /**
   * Die Bildhoehe in der Shopping-Kachel.
   *
   * `normal` - 128 px wie heute. `gross` - 192 px: die Kachel ist der
   * emotionale Anker der Karte, das Bild darf mehr Raum haben. Kostet genau
   * die 64 px Differenz in der linken Spalte.
   */
  kachelBild: 'normal' | 'gross';

  /**
   * Das Zeichen an den "Was hat sich geaendert"-Karten.
   *
   * `sparkle` - wie heute das Sparkle-Icon in Violett. Seit `chipMarke:
   * 'sparkle'` traegt die Karte dasselbe Zeichen aber doppelt: an den Chips
   * UND an jeder Aenderungskarte, wenige Zentimeter auseinander.
   * `haken` - der gruene Haken (CheckCircle2, dieselbe Form wie am
   * FeedSwitcher-Uploadstatus). Trennt die Icon-Sprache: Sparkle heisst
   * "das kam von der KI" (Chips), der Haken heisst "Verbesserung umgesetzt"
   * (Aenderungsliste). Dennis-Zuruf vom 24.08.2026.
   */
  wasGeaendertIcon: 'sparkle' | 'haken';

  /**
   * Die Form der "Was hat sich geaendert"-Eintraege.
   *
   * `karten` - wie heute: zweispaltiges Raster umrandeter Boxen.
   * `liste` - rahmenlose, einzeilige Checkliste (Richtung A der
   * Feinschliff-Mockups). Drei Zeilen statt drei Boxen: eine
   * Verschachtelungsebene weniger auf der Karte. Kombiniert frei mit
   * `wasGeaendertIcon` - Dennis will Boxen+Haken gegen Liste+Haken
   * vergleichen koennen (Zuruf 24.08.).
   * `chips` - eine Chip-Zeile (`flex flex-wrap`), je Marker nur der `titel`;
   * der Begruendungssatz (`text`) wandert ins `title`-Attribut UND ins
   * `aria-label` des Chips, damit er fuer Screenreader nicht verloren geht
   * (Farbe/Kuerzung allein sind keine Information). Am dichtesten von den
   * drei Formen, darum T5-Standard.
   */
  wasGeaendertLayout: 'karten' | 'liste' | 'chips';

  /**
   * Anordnung von Original und optimiertem Titel.
   *
   * `originalOben` - wie heute: Original durchgestrichen UEBER dem neuen.
   * `originalFussnote` - der neue Titel ist der Held (groessere Schrift in
   * der Verlaufbox, zuerst), das Original steht als durchgestrichene
   * Fussnote DARUNTER. Der Blick landet auf dem Ergebnis, nicht auf dem
   * Problem. Diff-Markierung und Kuerzungsstrich bleiben erhalten.
   * `inline` - EIN Textlauf, in dem Original UND neuer Titel als ein
   * einziger Diff nebeneinander stehen (`BeforeAfterDiff`-Variante
   * `'inline'`) - kein Vorher-Block, kein Nachher-Kasten, keine Fussnote.
   * Der Kuerzungsstrich (`cutAt`) hat hier keinen Traeger und wird ignoriert.
   *
   * Voreinstellung seit 02.10.2026: `originalFussnote` (Dennis' Wahl). Der
   * Textlauf zerlegte umgestellte Titel zu "DVD –Master Class Class DVD", und
   * den alten Titel am Stueck sah man nirgends; in der Fussnote steht er ganz.
   * Eine erprobte Variante "Vorher und Neu" als zwei Zeilen ohne Diff wurde
   * dabei verworfen (App-PR #416, geschlossen).
   */
  titelAnordnung: 'originalOben' | 'originalFussnote' | 'inline';

  /**
   * Darstellung der Google-Produktkategorie.
   *
   * `pille` - wie heute: als Chip im Attribut-Raster; bei langen Pfaden
   * waechst er zur mehrzeiligen Riesen-Pille (siehe Zwergehuus-Screenshot).
   * `zeile` - eine leise Schlusszeile unter den Chips: vorn gekuerzter Pfad
   * (das ENDE traegt die Information), letztes Segment fett, ID rechts.
   * Klick oeffnet weiter das Eingabefeld. Ein fehlender Wert bleibt als
   * Warn-Chip im Raster - eine leere Schlusszeile warnt niemanden.
   * `gestrichelterChip` - bleibt IM Chip-Raster (keine eigene Zeile
   * darunter), traegt aber `border-dashed` und zeigt nur das letzte Segment
   * des Pfades; der volle Pfad steht im `title` und im `aria-label`. Klick
   * oeffnet dasselbe Eingabefeld wie die anderen beiden Werte. Ein
   * fehlender Wert bleibt wie heute als Warn-Chip.
   */
  kategorieZeile: 'pille' | 'zeile' | 'gestrichelterChip';

  /**
   * Wo die Punktzahl wohnt. Dennis-Befund 24.08.: der Score erscheint
   * dreimal (Produktliste, linke Spalte, kuenftig Aenderungsblock) - einmal
   * reicht.
   *
   * `kachel` - wie heute: Punkte-Panel in der linken Spalte.
   * `imAenderungsblock` - die linke Spalte verliert das Panel; stattdessen
   * traegt "Was hat sich geaendert" rechts eine Badge "38 -> 96", Klick
   * klappt den vollen Breakdown auf. Die Zahl steht dann dort, wo auch die
   * Begruendung steht.
   */
  punkteOrt: 'kachel' | 'imAenderungsblock';
}

/**
 * Das heutige Verhalten, Punkt fuer Punkt. Ohne Provider gilt genau das -
 * also auf /workbench fuer alle Nutzer.
 *
 * BEFOERDERT am 24.08.2026 auf Dennis' ausdrueckliches "befoerdern":
 * STANDARD traegt seitdem seine auf der Entwurfsroute gewaehlte Mischung
 * (= AUSWAHL_DENNIS, dort dokumentiert). Der Stand von davor - die Werte
 * der Werkbank aus der Zeit vor der Karten-Entlastung - liegt vollstaendig
 * in der Git-Historie dieser Datei (Commit-Eltern von #268/#270) und als
 * benannte Vergleichswerte in den Schalter-Varianten der Komponententests.
 * Die zwei Perf-Minors aus dem Abschlussbericht (countTitleDiff und
 * weakestComponent liefen bei jedem Render) sind im selben Zug memoisiert.
 */
export const STANDARD: EntwurfOptionen = {
  kopfzone: 'eineZeile',
  seitentitel: 'sichtbar',
  statusAnzeige: 'marker',
  uploadBeschriftung: 'kurz',
  leistenBreite: 340,
  listenKachel: 'eigeneKachel',
  reiterStil: 'segmente',
  fortschritt: 'imKartenkopf',
  beschreibungEditor: 'hinterAnpassen',
  kachelVorherTitel: 'weglassen',
  kopftitelMarke: 'mitOptimiert',
  kuerzungsHinweisLinks: 'gemesseneZahl',
  kuerzungsAnzeige: 'keine',
  titelDiffTraeger: 'zaehlerImLabel',
  insMarke: 'neutral',
  punkteTiefe: 'eineZeile',
  feedSpalteDichte: 'kompakt',
  highlightsEditor: 'hinterAnpassen',
  punkteAnzeige: 'zahl',
  chipMarke: 'sparkle',
  leerzustand: 'erfolgsmoment',
  kachelBild: 'gross',
  wasGeaendertIcon: 'haken',
  wasGeaendertLayout: 'chips',
  titelAnordnung: 'originalFussnote',
  kategorieZeile: 'gestrichelterChip',
  punkteOrt: 'kachel',
};

/** Der Vorschlag, so wie ich ihn empfehle. Voreinstellung der Entwurfsseite. */
export const VORSCHLAG: EntwurfOptionen = {
  kopfzone: 'eineZeile',
  seitentitel: 'nurSchmal',
  statusAnzeige: 'marker',
  uploadBeschriftung: 'kurz',
  leistenBreite: 300,
  listenKachel: 'eigeneKachel',
  reiterStil: 'segmente',
  fortschritt: 'imKartenkopf',
  beschreibungEditor: 'hinterAnpassen',
  kachelVorherTitel: 'weglassen',
  kopftitelMarke: 'mitOptimiert',
  kuerzungsHinweisLinks: 'gemesseneZahl',
  kuerzungsAnzeige: 'keine',
  titelDiffTraeger: 'zaehlerImLabel',
  insMarke: 'neutral',
  punkteTiefe: 'eineZeile',
  feedSpalteDichte: 'kompakt',
  highlightsEditor: 'hinterAnpassen',
  punkteAnzeige: 'ring',
  chipMarke: 'sparkle',
  leerzustand: 'erfolgsmoment',
  kachelBild: 'gross',
  wasGeaendertIcon: 'haken',
  wasGeaendertLayout: 'chips',
  titelAnordnung: 'originalFussnote',
  kategorieZeile: 'gestrichelterChip',
  punkteOrt: 'kachel',
};

/**
 * Dennis' gewaehlte Mischung, gepinnt am 24.08.2026 (Quelle: sein
 * localStorage-Export aus der Entwurfsroute). Folgt dem Vorschlag bei 20 von
 * 27 Schaltern; die Abweichungen sind bewusste Entscheidungen:
 * Boxen MIT Haken statt Checkliste, nackte Zahl statt Ring, Seitentitel
 * sichtbar, Leiste 340 px. `punkteTiefe` und `punkteOrt` wurden mit T4
 * (08.09.2026) auf `eineZeile` bzw. `kachel` nachgezogen - die explorative
 * `imAenderungsblock`-Variante bleibt als Schalter erhalten, ist aber seither
 * kein Teil dieser Mischung mehr.
 * Kandidat fuer die Befoerderung in den Hauptpfad /workbench - dafuer
 * braucht es Dennis' ausdrueckliche Freigabe plus die zwei Perf-Minors
 * aus dem Abschlussbericht.
 */
export const AUSWAHL_DENNIS: EntwurfOptionen = {
  kopfzone: 'eineZeile',
  seitentitel: 'sichtbar',
  statusAnzeige: 'marker',
  uploadBeschriftung: 'kurz',
  leistenBreite: 340,
  listenKachel: 'eigeneKachel',
  reiterStil: 'segmente',
  fortschritt: 'imKartenkopf',
  beschreibungEditor: 'hinterAnpassen',
  kachelVorherTitel: 'weglassen',
  kopftitelMarke: 'mitOptimiert',
  kuerzungsHinweisLinks: 'gemesseneZahl',
  kuerzungsAnzeige: 'keine',
  titelDiffTraeger: 'zaehlerImLabel',
  insMarke: 'neutral',
  punkteTiefe: 'eineZeile',
  feedSpalteDichte: 'kompakt',
  highlightsEditor: 'hinterAnpassen',
  punkteAnzeige: 'zahl',
  chipMarke: 'sparkle',
  leerzustand: 'erfolgsmoment',
  kachelBild: 'gross',
  wasGeaendertIcon: 'haken',
  wasGeaendertLayout: 'chips',
  titelAnordnung: 'originalFussnote',
  kategorieZeile: 'gestrichelterChip',
  punkteOrt: 'kachel',
};

const Ctx = createContext<EntwurfOptionen>(STANDARD);

export function EntwurfProvider({
  optionen,
  children,
}: {
  optionen: EntwurfOptionen;
  children: ReactNode;
}) {
  return <Ctx.Provider value={optionen}>{children}</Ctx.Provider>;
}

/**
 * Liest die Entwurfsoptionen.
 *
 * Ausserhalb der Entwurfsseite gibt es keinen Provider, und dann liefert der
 * Haken `STANDARD` - also das heutige Verhalten. Deshalb duerfen ihn auch
 * Bauteile aufrufen, die im Hauptpfad laufen.
 */
export function useEntwurf(): EntwurfOptionen {
  return useContext(Ctx);
}
